/**
 * Rudrik Behavioral Analysis
 * ===========================
 * Compares customer's CURRENT behavior vs HISTORICAL baseline.
 * Two columns of inputs (baseline + current) + 4 sample scenarios.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Activity, BrainCircuit, AlertTriangle, ShieldCheck, ShieldAlert, Loader2,
  RefreshCw, MapPin, Clock, Users, Smartphone, TrendingUp, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/behavioral`;

const RISK_COLOR = { LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#f97316", CRITICAL: "#dc2626" };
const ACTION_COLOR = { ALLOW: "#22c55e", EXTRA_AUTH: "#f59e0b", INVESTIGATE: "#f97316", HOLD: "#f97316", BLOCK: "#dc2626" };
const SEV_COLOR = { CRITICAL: "#dc2626", HIGH: "#f97316", MEDIUM: "#f59e0b", LOW: "#22c55e" };
const ANOMALY_ICON = {
  AMOUNT: TrendingUp, VELOCITY: Activity, LOCATION: MapPin,
  BENEFICIARY: Users, TIME: Clock, CHANNEL: Smartphone,
};

function F({ label, value, onChange, type = "text", testId }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <input type={type} value={value} data-testid={testId}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value || 0) : e.target.value)}
        style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 10px", color: "#f1f5f9", fontSize: 12 }} />
    </label>
  );
}

function Section({ title, accent, children, testId }) {
  return (
    <section data-testid={testId} style={{ background: "#0d1117", border: `1px solid ${accent}55`, borderRadius: 10, padding: 14 }}>
      <div style={{ color: accent, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{title}</div>
      {children}
    </section>
  );
}

export default function BehavioralPage() {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Baseline
  const [customerId, setCustomerId] = useState("CUST-001");
  const [avgMonthly, setAvgMonthly] = useState(18);
  const [avgAmount, setAvgAmount] = useState(15000);
  const [usualCity, setUsualCity] = useState("Bengaluru");
  const [usualCountry, setUsualCountry] = useState("IN");
  const [usualTime, setUsualTime] = useState("9AM-9PM");
  const [usualChannels, setUsualChannels] = useState("UPI,NEFT");
  const [usualDevices, setUsualDevices] = useState("dev-A");

  // Current
  const [curTxn, setCurTxn] = useState(16);
  const [curAmount, setCurAmount] = useState(14000);
  const [curCity, setCurCity] = useState("Bengaluru");
  const [curCountry, setCurCountry] = useState("IN");
  const [curTime, setCurTime] = useState("10:30");
  const [newBen, setNewBen] = useState(0);
  const [benWeek, setBenWeek] = useState(0);
  const [curChannels, setCurChannels] = useState("UPI");
  const [curDevices, setCurDevices] = useState("dev-A");

  useEffect(() => {
    axios.get(`${API}/sample-scenarios`, { withCredentials: true })
      .then(({ data }) => setScenarios(data.scenarios || []))
      .catch(() => {});
  }, []);

  const applyScenario = (s) => {
    const b = s.payload.baseline_profile || {};
    const c = s.payload.current_behavior || {};
    setCustomerId(s.payload.customer_id || "");
    setAvgMonthly(b.avg_monthly_txn || 0); setAvgAmount(b.avg_txn_amount || 0);
    setUsualCity(b.usual_login_city || ""); setUsualCountry(b.usual_login_country || "IN");
    setUsualTime(b.usual_login_time || "9AM-9PM");
    setUsualChannels((b.usual_channels || []).join(",")); setUsualDevices((b.usual_devices || []).join(","));
    setCurTxn(c.this_month_txn || 0); setCurAmount(c.this_month_amount || 0);
    setCurCity(c.login_city || ""); setCurCountry(c.login_country || "IN");
    setCurTime(c.login_time || ""); setNewBen(c.new_beneficiaries || 0);
    setBenWeek(c.beneficiaries_this_week || 0);
    setCurChannels((c.channels_used || []).join(",")); setCurDevices((c.devices_used || []).join(","));
    setResult(null);
    toast.success(`Loaded: ${s.label}`);
  };

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const payload = {
        customer_id: customerId,
        analysis_period: "last_30_days",
        baseline_profile: {
          avg_monthly_txn: Number(avgMonthly) || 0,
          avg_txn_amount: Number(avgAmount) || 0,
          usual_login_city: usualCity, usual_login_country: usualCountry,
          usual_login_time: usualTime,
          usual_channels: usualChannels.split(",").map((s) => s.trim()).filter(Boolean),
          usual_devices: usualDevices.split(",").map((s) => s.trim()).filter(Boolean),
        },
        current_behavior: {
          this_month_txn: Number(curTxn) || 0,
          this_month_amount: Number(curAmount) || 0,
          login_city: curCity, login_country: curCountry,
          login_time: curTime,
          new_beneficiaries: Number(newBen) || 0,
          beneficiaries_this_week: Number(benWeek) || 0,
          channels_used: curChannels.split(",").map((s) => s.trim()).filter(Boolean),
          devices_used: curDevices.split(",").map((s) => s.trim()).filter(Boolean),
        },
      };
      const { data } = await axios.post(`${API}/analyze`, payload, { withCredentials: true, timeout: 25000 });
      setResult(data.result);
      const r = data.result;
      if (r.risk_level === "CRITICAL") toast.error(`${r.recommended_action} — CRITICAL · score ${r.anomaly_score}`);
      else if (r.risk_level === "HIGH") toast.warning(`${r.recommended_action} — HIGH · score ${r.anomaly_score}`);
      else if (r.risk_level === "MEDIUM") toast.warning(`${r.recommended_action} — MEDIUM`);
      else toast.success(`${r.recommended_action} — LOW`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setLoading(false); }
  };

  const riskColor = RISK_COLOR[result?.risk_level] || "#94a3b8";
  const actionColor = ACTION_COLOR[result?.recommended_action] || "#94a3b8";

  return (
    <div data-testid="behavioral-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-1">
        <BrainCircuit style={{ width: 26, height: 26, color: "#a855f7" }} />
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Behavioral Analysis</h1>
        <span data-testid="session-badge" style={{ background: "rgba(168,85,247,0.10)", color: "#c084fc", border: "1px solid #a855f755", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Session-only</span>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
        Compare a customer's 30-day current behavior vs historical baseline across 6 anomaly types.
        Surfaces account-takeover and money-mule patterns in seconds.
      </p>

      {/* Sample scenarios */}
      {scenarios.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          <span style={{ color: "#64748b", fontSize: 11, alignSelf: "center", marginRight: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <FlaskConical size={11} /> Quick demos:
          </span>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => applyScenario(s)} data-testid={`sample-${s.id}`}
              style={{ background: "#0d1117", border: "1px solid #1e2530", color: "#cbd5e1", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      <F label="Customer ID" value={customerId} onChange={setCustomerId} testId="f-cust-id" />
      <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Section title="Historical Baseline (12-mo avg)" accent="#22c55e" testId="form-baseline">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <F label="Avg monthly TXns" value={avgMonthly} onChange={setAvgMonthly} type="number" testId="b-avg-monthly" />
            <F label="Avg TXn amount (INR)" value={avgAmount} onChange={setAvgAmount} type="number" testId="b-avg-amount" />
            <F label="Usual login city" value={usualCity} onChange={setUsualCity} testId="b-city" />
            <F label="Usual country" value={usualCountry} onChange={(v) => setUsualCountry(v.toUpperCase().slice(0,2))} testId="b-country" />
            <F label="Usual login window" value={usualTime} onChange={setUsualTime} testId="b-time" />
            <F label="Usual channels (csv)" value={usualChannels} onChange={setUsualChannels} testId="b-channels" />
            <F label="Trusted devices (csv)" value={usualDevices} onChange={setUsualDevices} testId="b-devices" />
          </div>
        </Section>

        <Section title="Current Month Behavior" accent="#a855f7" testId="form-current">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <F label="TXns this month" value={curTxn} onChange={setCurTxn} type="number" testId="c-txn" />
            <F label="Amount this month (INR)" value={curAmount} onChange={setCurAmount} type="number" testId="c-amount" />
            <F label="Login city" value={curCity} onChange={setCurCity} testId="c-city" />
            <F label="Login country" value={curCountry} onChange={(v) => setCurCountry(v.toUpperCase().slice(0,2))} testId="c-country" />
            <F label="Login time (HH:MM)" value={curTime} onChange={setCurTime} testId="c-time" />
            <F label="New beneficiaries (month)" value={newBen} onChange={setNewBen} type="number" testId="c-newben" />
            <F label="New beneficiaries (this wk)" value={benWeek} onChange={setBenWeek} type="number" testId="c-benweek" />
            <F label="Channels used (csv)" value={curChannels} onChange={setCurChannels} testId="c-channels" />
            <F label="Devices used (csv)" value={curDevices} onChange={setCurDevices} testId="c-devices" />
          </div>
        </Section>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 16 }}>
        <button onClick={run} disabled={loading} data-testid="run-btn"
          style={{ flex: 1, background: loading ? "#1e2530" : "linear-gradient(135deg, #a855f7, #7e22ce)",
                   border: "none", color: "white", borderRadius: 6, padding: "11px 18px",
                   fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                   display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {loading ? <Loader2 className="animate-spin" size={14} /> : <BrainCircuit size={14} />}
          {loading ? "Analysing…" : "Analyse behavior"}
        </button>
      </div>

      {result && (
        <>
          {/* Hero verdict */}
          <div data-testid="behav-result" style={{
            background: "#0d1117", border: `1px solid ${actionColor}66`, borderRadius: 12, padding: 22, marginBottom: 12,
            display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 16, alignItems: "center",
          }}>
            <div>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Recommended Action</div>
              <div data-testid="action" style={{ color: actionColor, fontSize: 22, fontWeight: 800, marginTop: 4 }}>{result.recommended_action}</div>
              <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 6 }}>{result.summary}</div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 6 }}>
                {result.ai_mode} · {result.latency_ms}ms · period {result.analysis_period}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Anomaly Score</div>
              <div data-testid="score" style={{ color: riskColor, fontSize: 38, fontWeight: 800 }}>{result.anomaly_score}</div>
              <div data-testid="risk" style={{ color: riskColor, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>{result.risk_level}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>ATO Risk</div>
              <div data-testid="ato" style={{ color: result.account_takeover_risk ? "#fca5a5" : "#22c55e", fontSize: 14, fontWeight: 800, marginTop: 6 }}>
                {result.account_takeover_risk ? "Detected" : "Clear"}
              </div>
              <div style={{ color: "#64748b", fontSize: 10 }}>{result.account_takeover_signals?.length || 0} signals</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Mule Risk</div>
              <div data-testid="mule" style={{ color: result.money_mule_risk ? "#fca5a5" : "#22c55e", fontSize: 14, fontWeight: 800, marginTop: 6 }}>
                {result.money_mule_risk ? "Likely" : "Clear"}
              </div>
              <div style={{ color: "#64748b", fontSize: 10 }}>{result.anomalies?.length || 0} anomalies</div>
            </div>
          </div>

          {result.anomalies?.length > 0 && (
            <Section title={`Anomalies (${result.anomalies.length})`} accent="#f97316" testId="anomalies-section">
              <div style={{ display: "grid", gap: 6 }}>
                {result.anomalies.map((a, i) => {
                  const c = SEV_COLOR[a.severity] || "#94a3b8";
                  const Icon = ANOMALY_ICON[a.type] || AlertTriangle;
                  return (
                    <div key={i} data-testid={`anomaly-${i}`} style={{ background: "#080c12", border: `1px solid ${c}55`, borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon size={13} style={{ color: c }} />
                        <span style={{ color: c, fontSize: 11, fontWeight: 800 }}>{a.type}</span>
                        <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{a.severity} · Δ {a.deviation_percent}%</span>
                      </div>
                      <div style={{ marginTop: 4, color: "#cbd5e1", fontSize: 11 }}>
                        <span style={{ color: "#94a3b8" }}>Baseline:</span> {a.baseline} <span style={{ color: "#475569" }}>→</span> <span style={{ color: "#f1f5f9" }}>{a.current}</span>
                      </div>
                      <div style={{ marginTop: 2, color: "#94a3b8", fontSize: 11 }}>{a.explanation}</div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {(result.account_takeover_risk || result.money_mule_risk) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              {result.account_takeover_risk && (
                <Section title="Account Takeover Signals" accent="#dc2626" testId="ato-section">
                  <ul style={{ color: "#fca5a5", fontSize: 12, paddingLeft: 18, margin: 0, lineHeight: 1.7 }}>
                    {result.account_takeover_signals.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </Section>
              )}
              {result.money_mule_risk && (
                <Section title="Money-Mule Pattern" accent="#f97316" testId="mule-section">
                  <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.6 }}>
                    Composite of velocity surge + amount spike + multiple new beneficiaries — classic mule fan-out signature. Investigate beneficiary network and recent fund-flow.
                  </div>
                </Section>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
